import { CCol, CFormGroup, CInput, CInputGroup, CLabel, CRow } from '@coreui/react';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import React, { useState } from 'react';
import _t from 'counterpart';
import { useGetSetting } from './hooks';
import { findErrorFromValidation, handleFormError } from '../../helpers';
import { useMutation } from 'react-query';
import { updateCommpeak } from '../../services/BackendService';
import toast from 'react-hot-toast';

interface IParams {
  url: string;
  crmId: string;
  c2cId: string;
  prefix: string;
  username: string;
  password: string;
}

interface IProps {
  onChange: () => void;
}

const CommpeakIntegration = ({ onChange }: IProps) => {
  const [url, setUrl] = useState<string>('');
  const [crmId, setCrmId] = useState<string>('');
  const [c2cId, setC2cId] = useState<string>('');
  const [prefix, setPrefix] = useState<string>('00');
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const requestUrlQuery = useGetSetting('COMMPEAK_REQUEST_URL', 'commpeak-request-url', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => setUrl(value),
  });

  const crmIdQuery = useGetSetting('COMMPEAK_CRM_ID', 'commpeak-crm-id', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => setCrmId(value),
  });

  const c2cIdQuery = useGetSetting('COMMPEAK_C2C_ID', 'commpeak-c2c-id', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => setC2cId(value),
  });

  const prefixQuery = useGetSetting('COMMPEAK_PREFIX', 'commpeak-prefix', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => setPrefix(value || '00'),
  });

  const usernameQuery = useGetSetting('COMMPEAK_USERNAME', 'commpeak-username', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => setUsername(value || ''),
  });

  const passwordQuery = useGetSetting('COMMPEAK_PASSWORD', 'commpeak-password', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => setPassword(value || ''),
  });

  const updateCommpeakMutation = useMutation(
    ({ url, crmId, c2cId, prefix, username, password }: IParams) =>
      updateCommpeak(url, crmId, c2cId, prefix, username, password),
    {
      onError: (error: any) => {
        handleFormError(error);
      },
      onSuccess: () => {
        toast.success(_t('commpeak.configured'));
        onChange();
      },
    }
  );

  const callUpdateMutation = () => {
    updateCommpeakMutation.mutate({ url, crmId, c2cId, prefix: prefix || '00', username, password });
  };

  const findError = (paramName: string) => {
    return findErrorFromValidation(updateCommpeakMutation.error, paramName);
  };

  return (
    <>
      <CRow>
        <CCol>
          <p>{_t('commpeak.paragraph-one')}</p>
          <p>{_t('commpeak.paragraph-two-basic-auth')}</p>
          <p>{_t('commpeak.paragraph-three')}</p>
        </CCol>
      </CRow>
      <CRow>
        <CCol md={12}>
          <CFormGroup>
            <CLabel htmlFor="url">{_t('commpeak.url')}</CLabel>
            <CInputGroup>
              <CInput
                type="text"
                id="url"
                name="url"
                value={url}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
                className="instrument-input-height"
              />
            </CInputGroup>
            {requestUrlQuery.error && <CLabel className="text-danger">{_t('commpeak.url-failed')}</CLabel>}
            {findError('url') && <CLabel className="text-danger">{findError('url')}</CLabel>}
          </CFormGroup>
        </CCol>
        <CCol md={12}>
          <CFormGroup>
            <CLabel htmlFor="crmId">{_t('commpeak.crm-id')}</CLabel>
            <CInputGroup>
              <CInput
                type="text"
                id="crmId"
                name="crmId"
                value={crmId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCrmId(e.target.value)}
                className="instrument-input-height"
              />
            </CInputGroup>
            {crmIdQuery.error && <CLabel className="text-danger">{_t('commpeak.crm-id-failed')}</CLabel>}
            {findError('crmId') && <CLabel className="text-danger">{findError('crmId')}</CLabel>}
          </CFormGroup>
        </CCol>
        <CCol md={12}>
          <CFormGroup>
            <CLabel htmlFor="c2cId">{_t('commpeak.c2c-id')}</CLabel>
            <CInputGroup>
              <CInput
                type="text"
                id="c2cId"
                name="c2cId"
                value={c2cId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setC2cId(e.target.value)}
                className="instrument-input-height"
              />
            </CInputGroup>
            {c2cIdQuery.error && <CLabel className="text-danger">{_t('commpeak.c2c-id-failed')}</CLabel>}
            {findError('c2cId') && <CLabel className="text-danger">{findError('c2cId')}</CLabel>}
          </CFormGroup>
        </CCol>
        <CCol md={12}>
          <CFormGroup>
            <CLabel htmlFor="prefix">{_t('commpeak.prefix')}</CLabel>
            <CInputGroup>
              <CInput
                type="text"
                id="prefix"
                name="prefix"
                value={prefix}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrefix(e.target.value)}
                className="instrument-input-height"
              />
            </CInputGroup>
            {prefixQuery.error && <CLabel className="text-danger">{_t('commpeak.prefix-failed')}</CLabel>}
            {findError('prefix') && <CLabel className="text-danger">{findError('prefix')}</CLabel>}
          </CFormGroup>
        </CCol>
        <CCol md={12}>
          <CFormGroup>
            <CLabel htmlFor="username">{_t('commpeak.username')}</CLabel>
            <CInputGroup>
              <CInput
                type="text"
                id="username"
                name="username"
                value={username}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
                className="instrument-input-height"
              />
            </CInputGroup>
            {usernameQuery.error && (
              <CLabel className="text-danger">{_t('commpeak.username-failed')}</CLabel>
            )}
            {findError('username') && <CLabel className="text-danger">{findError('username')}</CLabel>}
          </CFormGroup>
        </CCol>
        <CCol md={12}>
          <CFormGroup>
            <CLabel htmlFor="password">{_t('commpeak.password')}</CLabel>
            <CInputGroup>
              <CInput
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                className="instrument-input-height"
              />
            </CInputGroup>
            {passwordQuery.error && (
              <CLabel className="text-danger">{_t('commpeak.password-failed')}</CLabel>
            )}
            {findError('password') && <CLabel className="text-danger">{findError('password')}</CLabel>}
          </CFormGroup>
        </CCol>
        <CCol md={12} className="d-flex justify-content-end">
          <ButtonWithLoader
            isLoading={updateCommpeakMutation.isLoading}
            onClick={callUpdateMutation}
            buttonColor="primary"
            spinnerColor="secondary"
            title={_t('action.save')}
            className="btn-md"
            showText={false}
            disabled={!url || !crmId || !c2cId || !username || !password}
          />
        </CCol>
      </CRow>
    </>
  );
};

export default CommpeakIntegration;
